<template lang="pug">
  v-data-table(ref="dataTable" v-bind="$attrs" v-on="$listeners" :style="dataTableStyle" :items-per-page="itemsPerPage" :hide-default-footer="hideDefaultFooter" :header-props="headerProps" class="yz-data-table")
    template(v-for="(slot, name) in $scopedSlots" :slot="name" slot-scope="scope")
      slot(:name="name" v-bind="scope")
</template>

<script>
export default {
  name: "YzDataTable",
  props: {
    radius: {
      type: [String, Number],
      default: '10px'
    },
    headerBackgroundColor: {
      type: String,
      default: 'yz-neutral-200'
    },
    bodyBackgroundColor: {
      type: String,
      default: 'white'
    },
    borderColor: {
      type: String,
      default: 'yz-neutral-300'
    },
    headerHeight: {
      type: [String, Number],
      default: '32px'
    },
    bodyHeight: {
      type: [String, Number],
      default: '40px'
    },
    hideDefaultFooter: {
      type: Boolean,
      default: true
    },
    headerClass: {
      type: String,
      default: 'yz-table-header'
    },
    bodyClass: {
      type: String,
      default: 'yz-medium-body-sm'
    },
    itemsPerPage: {
      type: Number,
      default: -1
    },
    headerProps: {
      type: Object,
      default: () => ({ sortIcon: '$yzi-triangle' })
    },
    sortIconSize: {
      type: [String, Number],
      default: '8px'
    },
    noOuterBorder: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      observer: null
    }
  },
  computed: {
    dataTableStyle() {
      const style = {}

      style.borderRadius = typeof this.radius === 'number' ? `${this.radius}px` : this.radius

      if (this.headerBackgroundColor) {
        style['--yz-datatable-header-bg'] = this.getFormattedColor(this.headerBackgroundColor)
      }

      if (this.bodyBackgroundColor) {
        style['--yz-datatable-body-bg'] = this.getFormattedColor(this.bodyBackgroundColor)
      }

      style['--yz-datatable-border-color'] = this.getFormattedColor(this.borderColor)
      style['--yz-datatable-header-height'] = typeof this.headerHeight === 'number' ? `${this.headerHeight}px` : this.headerHeight
      style['--yz-datatable-body-height'] = typeof this.bodyHeight === 'number' ? `${this.bodyHeight}px` : this.bodyHeight

      if (!this.noOuterBorder) {
        style.border = `1px solid ${this.getFormattedColor(this.borderColor)}`
      }
      style.overflow = 'hidden'

      return style
    }
  },
  mounted() {
    this.injectStyles()
    this.setupMutationObserver()

    // Add unique identifier to component
    this.$el.setAttribute('data-yz-table-id', `table-${this._uid}`)
  },
  watch: {
    headerClass() {
      this.injectStyles()
      this.applyTypographyStyles()
    },
    bodyClass() {
      this.injectStyles()
      this.applyTypographyStyles()
    },
    borderColor() {
      this.injectStyles()
    },
    sortIconSize() {
      this.injectStyles()
    }
  },
  beforeDestroy() {
    const styleId = `yz-data-table-styles-${this._uid}`
    const styleTag = document.getElementById(styleId)
    if (styleTag) {
      styleTag.remove()
    }

    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    getFormattedColor(color) {
      if (!color) return null
      if (color.startsWith('#')) return color
      if (color.startsWith('var(')) return color
      if (color.startsWith('yz-')) return `var(--${color})`
      return `var(--yz-${color})`
    },
    getTypographyVariables(className) {
      // Convert class name to CSS variable prefix
      // yz-medium-body-xs -> yz-font-medium-body-xs
      const prefix = className.replace('yz-', 'yz-font-')

      const rootStyles = getComputedStyle(document.documentElement)

      return {
        fontFamily: rootStyles.getPropertyValue('--yz-font-family').trim() || "'Inter', sans-serif",
        fontSize: rootStyles.getPropertyValue(`--${prefix}-size`).trim() || (className.includes('xs') ? '12px' : '14px'),
        fontWeight: rootStyles.getPropertyValue(`--${prefix}-weight`).trim() || '500',
        lineHeight: rootStyles.getPropertyValue(`--${prefix}-line-height`).trim() || '20px',
        letterSpacing: rootStyles.getPropertyValue(`--${prefix}-letter-spacing`).trim() || '0'
      }
    },
    setupMutationObserver() {
      // Watch for DOM changes and reapply styles
      this.observer = new MutationObserver(() => {
        this.applyTypographyStyles()
      })

      this.observer.observe(this.$el, {
        childList: true,
        subtree: true,
        attributes: false
      })

      // Initial application
      this.applyTypographyStyles()
    },
    applyTypographyStyles() {
      this.$nextTick(() => {
        // Apply to headers
        const headers = this.$el.querySelectorAll('.v-data-table-header th, thead th')
        headers.forEach(th => {
          if (!th.classList.contains(this.headerClass)) {
            th.classList.add(this.headerClass)
          }
        })

        // Apply to body cells
        const cells = this.$el.querySelectorAll('tbody td')
        cells.forEach(td => {
          if (!td.classList.contains(this.bodyClass)) {
            td.classList.add(this.bodyClass)
          }
        })
      })
    },
    injectStyles() {
      const styleId = `yz-data-table-styles-${this._uid}`
      let styleTag = document.getElementById(styleId)

      if (!styleTag) {
        styleTag = document.createElement('style')
        styleTag.id = styleId
        document.head.appendChild(styleTag)
      }

      const borderColor = this.getFormattedColor(this.borderColor)
      const headerStyles = this.getTypographyVariables(this.headerClass)
      const bodyStyles = this.getTypographyVariables(this.bodyClass)

      // Create very specific selectors that override Vuetify
      styleTag.innerHTML = `
        /* Override Vuetify's default typography completely - but exclude progress bar */
        [data-yz-table-id="table-${this._uid}"].v-data-table > .v-data-table__wrapper > table > thead > tr:not(.v-data-table__progress) > th {
          font-family: ${headerStyles.fontFamily} !important;
          font-size: ${headerStyles.fontSize} !important;
          font-weight: ${headerStyles.fontWeight} !important;
          line-height: ${headerStyles.lineHeight} !important;
          letter-spacing: ${headerStyles.letterSpacing} !important;
          height: var(--yz-datatable-header-height) !important;
          background-color: var(--yz-datatable-header-bg) !important;
          border-bottom: thin solid ${borderColor} !important;
          text-transform: none !important;
        }

        /* Specific styles for progress bar */
        [data-yz-table-id="table-${this._uid}"] .v-data-table__progress {
          height: auto !important;
          padding: 0 !important;
          border: none !important;
          background: transparent !important;
        }

        [data-yz-table-id="table-${this._uid}"] .v-data-table__progress .v-progress-linear {
          height: 2px !important;
        }

        [data-yz-table-id="table-${this._uid}"].v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
          font-family: ${bodyStyles.fontFamily} !important;
          font-size: ${bodyStyles.fontSize} !important;
          font-weight: ${bodyStyles.fontWeight} !important;
          line-height: ${bodyStyles.lineHeight} !important;
          letter-spacing: ${bodyStyles.letterSpacing} !important;
          height: var(--yz-datatable-body-height) !important;
          background-color: var(--yz-datatable-body-bg) !important;
        }

        /* Ensure row height is respected */
        [data-yz-table-id="table-${this._uid}"] tbody tr {
          height: var(--yz-datatable-body-height) !important;
        }

        /* Row borders */
        [data-yz-table-id="table-${this._uid}"] tbody tr:not(:last-child) td {
          border-bottom: thin solid ${borderColor} !important;
        }

        /* Apply correct styles to nested elements */
        [data-yz-table-id="table-${this._uid}"] th * {
          font-family: ${headerStyles.fontFamily} !important;
          font-size: ${headerStyles.fontSize} !important;
          font-weight: ${headerStyles.fontWeight} !important;
          line-height: ${headerStyles.lineHeight} !important;
          letter-spacing: ${headerStyles.letterSpacing} !important;
        }

        /* Ensure the classes themselves work when applied */
        [data-yz-table-id="table-${this._uid}"] .${this.headerClass} {
          font-family: ${headerStyles.fontFamily} !important;
          font-size: ${headerStyles.fontSize} !important;
          font-weight: ${headerStyles.fontWeight} !important;
          line-height: ${headerStyles.lineHeight} !important;
          letter-spacing: ${headerStyles.letterSpacing} !important;
        }

        [data-yz-table-id="table-${this._uid}"] .${this.bodyClass} {
          font-family: ${bodyStyles.fontFamily} !important;
          font-size: ${bodyStyles.fontSize} !important;
          font-weight: ${bodyStyles.fontWeight} !important;
          line-height: ${bodyStyles.lineHeight} !important;
          letter-spacing: ${bodyStyles.letterSpacing} !important;
        }

        /* Sortable header icon size */
        [data-yz-table-id="table-${this._uid}"] .v-data-table-header th.sortable .v-data-table-header__icon {
          width: ${typeof this.sortIconSize === 'number' ? `${this.sortIconSize}px` : this.sortIconSize} !important;
          height: ${typeof this.sortIconSize === 'number' ? `${this.sortIconSize}px` : this.sortIconSize} !important;
          margin-left: 3px !important;
        }
      `
    }
  }
}
</script>

<style scoped>
::v-deep .yz-data-table.v-data-table {
  background-color: transparent;
}

::v-deep .yz-data-table .v-data-table__wrapper {
  border: none !important;
  overflow: visible;
}

::v-deep .yz-data-table .v-data-table__wrapper > table {
  border: none !important;
  width: 100%;
}

/* Fix progress bar height */
::v-deep .yz-data-table .v-data-table__progress {
  height: auto !important;
}

::v-deep .yz-data-table .v-data-table__progress .v-progress-linear {
  height: 2px !important;
  margin: 0 !important;
}

::v-deep .yz-data-table .v-data-table__progress th {
  height: auto !important;
  padding: 0 !important;
  border: none !important;
}

/* Rounded corners */
::v-deep .yz-data-table .v-data-table-header th:first-child {
  border-top-left-radius: calc(var(--radius, 10px) - 1px);
}

::v-deep .yz-data-table .v-data-table-header th:last-child {
  border-top-right-radius: calc(var(--radius, 10px) - 1px);
}

::v-deep .yz-data-table tbody tr:last-child td:first-child {
  border-bottom-left-radius: calc(var(--radius, 10px) - 1px);
}

::v-deep .yz-data-table tbody tr:last-child td:last-child {
  border-bottom-right-radius: calc(var(--radius, 10px) - 1px);
}
</style>